import { useEffect } from "react";
import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";
import { isOneTrustEnabled, notifyConsentChanged as dispatchConsentChanged } from "@/services/consent";

export const ONETRUST_SCRIPT_ID = "onetrust-sdk";
const DEFAULT_SCRIPT_SRC = "https://cdn.cookielaw.org/scripttemplates/otSDKStub.js";

// When OneTrust is configured it is the only consent manager: there is no fallback to the
// legacy dialog. If it cannot load, no consent is given and nothing consent-gated runs.
const OneTrust = () => {
  const settings = useSettingsContext();
  const oneTrust = settings?.consentManager?.oneTrust;
  const enabled = isOneTrustEnabled(settings);
  const domainScript = oneTrust?.domainScript;
  const scriptSrc = oneTrust?.scriptSrc;

  useEffect(() => {
    if (!enabled || !domainScript) return;

    // OneTrust calls both OptanonWrapper and OneTrustGroupsUpdated with the same groups;
    // notify only real changes so consumers do not run twice.
    let lastNotifiedGroups: string | undefined;
    const notifyConsentChanged = () => {
      if (window.OptanonActiveGroups === lastNotifiedGroups) return;
      lastNotifiedGroups = window.OptanonActiveGroups;
      dispatchConsentChanged();
    };
    const previousOptanonWrapper = window.OptanonWrapper;

    // OptanonWrapper is called once the SDK has loaded and on every consent change.
    window.OptanonWrapper = () => {
      previousOptanonWrapper?.();
      notifyConsentChanged();
    };
    window.addEventListener("OneTrustGroupsUpdated", notifyConsentChanged);

    if (!document.getElementById(ONETRUST_SCRIPT_ID)) {
      const script = document.createElement("script");
      script.id = ONETRUST_SCRIPT_ID;
      script.src = scriptSrc ?? DEFAULT_SCRIPT_SRC;
      script.type = "text/javascript";
      script.charset = "UTF-8";
      script.dataset.documentLanguage = "true";
      script.dataset.domainScript = domainScript;
      script.onerror = () => {
        console.error("OneTrust could not be loaded; no consent will be given");
      };
      document.head.prepend(script);
    }

    return () => {
      window.removeEventListener("OneTrustGroupsUpdated", notifyConsentChanged);
      window.OptanonWrapper = previousOptanonWrapper;
    };
  }, [domainScript, enabled, scriptSrc]);

  return null;
};

export default OneTrust;
